import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import axios from "axios";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faChalkboardTeacher,
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faCheck,
  faList,
  faUserTie,
  faGraduationCap,
  faLink,
  faSync,
  faSearch,
  faFilter,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
  faCopy,
  faUpload,
  faDownload,
  faTimesCircle
} from "@fortawesome/free-solid-svg-icons";
import "./SubjectManagement.css";

const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

const SubjectManagement = () => {
  const [activeTab, setActiveTab] = useState("subjects");
  const [isLoading, setIsLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  
  // Bulk creation states
  const [bulkSubjects, setBulkSubjects] = useState([{ name: "", code: "", description: "" }]);
  const [bulkClass, setBulkClass] = useState("");
  const [bulkStatus, setBulkStatus] = useState("active");
  
  // Form states
  const [newSubject, setNewSubject] = useState({
    name: "",
    code: "",
    class: "",
    description: "",
    status: "active"
  });
  
  const [editMode, setEditMode] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [viewingSubject, setViewingSubject] = useState(null);
  
  // Assignment states
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [tempSelectedSubjects, setTempSelectedSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");
  const [modalSubjectSearch, setModalSubjectSearch] = useState("");
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [availableSubjectsSearch, setAvailableSubjectsSearch] = useState("");

  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  const statusOptions = [
    { value: "active", label: "Active", color: "success" },
    { value: "inactive", label: "Inactive", color: "danger" },
    { value: "archived", label: "Archived", color: "warning" }
  ];

  // Show warning alert function
  const showWarningAlert = (title, text, confirmText = "OK") => {
    return Swal.fire({
      title,
      text,
      icon: 'warning',
      iconColor: '#f59e0b',
      confirmButtonText: confirmText,
      confirmButtonColor: '#f59e0b',
      background: '#fff',
      customClass: {
        popup: 'subject-management-warning-popup',
        confirmButton: 'subject-management-warning-btn'
      }
    });
  };

  // Show success alert function
  const showSuccessAlert = (title, text, timer = 1500) => {
    return Swal.fire({
      icon: "success",
      title,
      text,
      timer,
      showConfirmButton: false
    });
  };

  // Show error alert function (only for critical errors)
  const showErrorAlert = (title, text) => {
    return Swal.fire({
      title,
      text,
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: '#ef4444',
      background: '#fff',
      customClass: {
        popup: 'subject-management-error-popup',
        confirmButton: 'subject-management-error-btn'
      }
    });
  };

  // Check for duplicate subjects
  const checkForDuplicates = (subjectsToCheck) => {
    const duplicates = [];
    const seen = new Set();
    
    subjectsToCheck.forEach((subject, index) => {
      const key = `${subject.name.toLowerCase()}_${subject.code.toLowerCase()}_${subject.class}`;
      if (seen.has(key)) {
        duplicates.push(index + 1);
      } else {
        seen.add(key);
      }
    });
    
    return duplicates;
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSubjects();
    fetchTeachers();
    fetchAssignedSubjects();
  }, []);

  const fetchSubjects = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/subjectCrudOP.php?action=fetch-subjects`
      );
      if (response.data.success) {
        setSubjects(response.data.data);
      } else {
        showWarningAlert("Warning", response.data.error || "Failed to load subjects");
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      showErrorAlert("Critical Error", "Failed to connect to server. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/subjectCrudOP.php?action=fetch-teachers`
      );
      if (response.data.success) {
        setTeachers(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching teachers:", error);
    }
  };

  const fetchAssignedSubjects = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/subjectCrudOP.php?action=fetch-assignments`
      );
      if (response.data.success) {
        setAssignedSubjects(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
    }
  };

  // Handle single subject creation
  const handleSubjectSubmit = async (e) => {
    e.preventDefault();
    
    if (!newSubject.name || !newSubject.code || !newSubject.class) {
      showWarningAlert("Validation Required", "Please fill all required fields (Name, Code, and Class)");
      return;
    }

    // Check for duplicate subject in existing subjects
    const isDuplicate = subjects.some(subject => 
      subject.name.toLowerCase() === newSubject.name.toLowerCase() &&
      subject.code.toLowerCase() === newSubject.code.toLowerCase() &&
      subject.class === newSubject.class
    );

    if (isDuplicate && !editMode) {
      showWarningAlert(
        "Duplicate Subject",
        `Subject "${newSubject.name}" with code "${newSubject.code}" already exists in ${newSubject.class}.`
      );
      return;
    }

    setIsLoading(true);
    try {
      const action = editMode ? "update-subject" : "create-subject";
      const data = editMode 
        ? { ...newSubject, id: editingSubject.id }
        : newSubject;

      const response = await axios.post(
        `${API_BASE_URL}/subjectCrudOP.php?action=${action}`,
        data
      );

      if (response.data.success) {
        showSuccessAlert(
          editMode ? "Updated Successfully!" : "Created Successfully!",
          response.data.message
        );
        
        setNewSubject({ 
          name: "", 
          code: "", 
          class: "", 
          description: "", 
          status: "active" 
        });
        setEditMode(false);
        setEditingSubject(null);
        setShowUpdateModal(false);
        fetchSubjects();
      } else {
        if (response.data.error.includes("duplicate") || response.data.error.includes("already exists")) {
          showWarningAlert("Duplicate Entry", response.data.error);
        } else {
          showWarningAlert("Operation Failed", response.data.error || "Operation failed");
        }
      }
    } catch (error) {
      if (error.response?.data?.error?.includes("duplicate")) {
        showWarningAlert("Duplicate Entry", "This subject already exists in the system.");
      } else {
        showErrorAlert("Error", error.message || "An unexpected error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk subject creation
  const handleBulkCreate = async (e) => {
    e.preventDefault();
    
    if (!bulkClass) {
      showWarningAlert("Class Required", "Please select a class for all subjects");
      return;
    }

    // Validate bulk subjects
    const validSubjects = bulkSubjects.filter(subject => 
      subject.name.trim() && subject.code.trim()
    );

    if (validSubjects.length === 0) {
      showWarningAlert("No Valid Subjects", "Please add at least one subject with name and code");
      return;
    }

    // Check for duplicates in bulk submission
    const duplicates = checkForDuplicates(validSubjects);
    if (duplicates.length > 0) {
      showWarningAlert(
        "Duplicate Subjects Found",
        `Rows ${duplicates.join(', ')} contain duplicate subject entries. Please remove duplicates before proceeding.`
      );
      return;
    }

    // Check for duplicates with existing subjects
    const existingDuplicates = [];
    validSubjects.forEach((subject, index) => {
      const isDuplicate = subjects.some(existing => 
        existing.name.toLowerCase() === subject.name.toLowerCase() &&
        existing.code.toLowerCase() === subject.code.toLowerCase() &&
        existing.class === bulkClass
      );
      if (isDuplicate) {
        existingDuplicates.push(index + 1);
      }
    });

    if (existingDuplicates.length > 0) {
      const result = await showWarningAlert(
        "Duplicate Subjects Detected",
        `Rows ${existingDuplicates.join(', ')} contain subjects that already exist in ${bulkClass}. Would you like to skip these and continue?`,
        "Skip Duplicates & Continue"
      );
      
      if (!result.isConfirmed) {
        return;
      }
    }

    setIsLoading(true);
    try {
      const subjectsToCreate = validSubjects
        .filter((subject, index) => !existingDuplicates.includes(index + 1))
        .map(subject => ({
          name: subject.name.trim(),
          code: subject.code.trim(),
          class: bulkClass,
          description: subject.description.trim(),
          status: bulkStatus
        }));

      if (subjectsToCreate.length === 0) {
        showWarningAlert("No New Subjects", "All subjects already exist in the system.");
        return;
      }

      // Create each subject individually
      const createPromises = subjectsToCreate.map(subject =>
        axios.post(
          `${API_BASE_URL}/subjectCrudOP.php?action=create-subject`,
          subject
        )
      );

      const results = await Promise.all(createPromises);
      const failed = results.filter(r => !r.data.success);
      const duplicateErrors = results.filter(r => 
        r.data.error?.includes("duplicate") || r.data.error?.includes("already exists")
      );

      if (failed.length === 0) {
        const successCount = subjectsToCreate.length - duplicateErrors.length;
        const duplicateCount = duplicateErrors.length;
        
        let message = `Successfully created ${successCount} subjects`;
        if (duplicateCount > 0) {
          message += ` (${duplicateCount} duplicates were skipped)`;
        }
        
        showSuccessAlert("Subjects Created!", message);
        
        // Reset form
        setBulkSubjects([{ name: "", code: "", description: "" }]);
        setBulkClass("");
        setBulkStatus("active");
        fetchSubjects();
      } else {
        const errorMessages = failed.map(r => r.data.error).filter(Boolean);
        showWarningAlert(
          "Partial Success",
          `${failed.length} subjects failed to create. ${errorMessages.join(', ')}`
        );
      }
    } catch (error) {
      showErrorAlert("Error", error.message || "Failed to create subjects");
    } finally {
      setIsLoading(false);
    }
  };

  // Add more rows for bulk creation
  const addMoreRows = () => {
    setBulkSubjects([...bulkSubjects, { name: "", code: "", description: "" }]);
  };

  // Update bulk subject row
  const updateBulkRow = (index, field, value) => {
    const updated = [...bulkSubjects];
    updated[index][field] = value;
    setBulkSubjects(updated);
  };

  // Remove bulk subject row with confirmation if data exists
  const removeBulkRow = async (index) => {
    if (bulkSubjects.length <= 1) {
      showWarningAlert("Cannot Remove", "At least one row is required");
      return;
    }

    const row = bulkSubjects[index];
    const hasData = row.name.trim() || row.code.trim() || row.description.trim();

    if (hasData) {
      const result = await Swal.fire({
        title: "Remove Row?",
        text: "This row contains data. Are you sure you want to remove it?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f59e0b",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Yes, remove it!",
        cancelButtonText: "Cancel",
        customClass: {
          popup: 'subject-management-confirmation-popup'
        }
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    const updated = bulkSubjects.filter((_, i) => i !== index);
    setBulkSubjects(updated);
  };

  const handleViewSubject = (subject) => {
    setViewingSubject(subject);
    setShowViewModal(true);
  };

  const handleEditSubject = (subject) => {
    setEditMode(true);
    setEditingSubject(subject);
    setNewSubject({
      name: subject.name,
      code: subject.code,
      class: subject.class,
      description: subject.description || "",
      status: subject.status || "active"
    });
    setShowUpdateModal(true);
  };

  const handleDeleteSubject = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This subject will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        popup: 'subject-management-confirmation-popup'
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/subjectCrudOP.php?action=delete-subject`,
          { id }
        );

        if (response.data.success) {
          showSuccessAlert("Deleted!", response.data.message);
          fetchSubjects();
        } else {
          showWarningAlert("Delete Failed", response.data.error || "Failed to delete subject");
        }
      } catch (error) {
        showErrorAlert("Error", error.message);
      }
    }
  };

  // Handle subject selection for assignment
  const handleSubjectSelection = (subjectId) => {
    setSelectedSubjects(prev => {
      if (prev.includes(subjectId)) {
        return prev.filter(id => id !== subjectId);
      } else {
        return [...prev, subjectId];
      }
    });
  };

  const handleTempSubjectSelection = (subjectId) => {
    setTempSelectedSubjects(prev => {
      if (prev.includes(subjectId)) {
        return prev.filter(id => id !== subjectId);
      } else {
        return [...prev, subjectId];
      }
    });
  };

  // Open assignment modal
  const openAssignmentModal = () => {
    if (selectedSubjects.length === 0) {
      showWarningAlert("No Subjects Selected", "Please select at least one subject to assign");
      return;
    }
    
    setTempSelectedSubjects([...selectedSubjects]);
    setShowAssignmentModal(true);
  };

  // Handle assignment
  const handleAssignSubjects = async () => {
    if (!selectedTeacher) {
      showWarningAlert("Teacher Required", "Please select a teacher");
      return;
    }

    if (!selectedClass) {
      showWarningAlert("Class Required", "Please select a class");
      return;
    }

    if (tempSelectedSubjects.length === 0) {
      showWarningAlert("No Subjects Selected", "Please select at least one subject");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/subjectCrudOP.php?action=assign-subjects`,
        {
          teacher_id: selectedTeacher,
          subject_ids: tempSelectedSubjects,
          class: selectedClass
        }
      );

      if (response.data.success) {
        showSuccessAlert("Assigned!", response.data.message);
        
        setShowAssignmentModal(false);
        setSelectedSubjects([]);
        setSelectedTeacher("");
        setSelectedClass("");
        setTempSelectedSubjects([]);
        fetchAssignedSubjects();
      } else {
        if (response.data.error.includes("already assigned")) {
          showWarningAlert("Already Assigned", response.data.error);
        } else {
          showWarningAlert("Assignment Failed", response.data.error || "Assignment failed");
        }
      }
    } catch (error) {
      showErrorAlert("Error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle remove assignment
  const handleRemoveAssignment = async (assignmentId) => {
    const result = await Swal.fire({
      title: "Remove Assignment?",
      text: "This subject will be unassigned from the teacher",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
      customClass: {
        popup: 'subject-management-confirmation-popup'
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/subjectCrudOP.php?action=remove-assignment`,
          { id: assignmentId }
        );

        if (response.data.success) {
          showSuccessAlert("Removed!", response.data.message);
          fetchAssignedSubjects();
        } else {
          showWarningAlert("Remove Failed", response.data.error || "Failed to remove assignment");
        }
      } catch (error) {
        showErrorAlert("Error", error.message);
      }
    }
  };

  // Filter subjects for main table
  const filteredSubjects = subjects.filter(subject => {
    const matchesSearch = subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subject.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = filterClass ? subject.class === filterClass : true;
    const matchesStatus = filterStatus ? subject.status === filterStatus : true;
    return matchesSearch && matchesClass && matchesStatus;
  });

  // Filter assignments
  const filteredAssignments = assignedSubjects.filter(assignment => {
    const matchesSearch = assignment.subject_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.teacher_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = filterClass ? assignment.class === filterClass : true;
    return matchesSearch && matchesClass;
  });

  // Get subjects not yet assigned
  const availableSubjects = subjects.filter(subject => 
    !assignedSubjects.some(assigned => assigned.subject_id === subject.id)
  );

  // Filter available subjects for assign tab
  const filteredAvailableSubjects = availableSubjects.filter(subject => {
    const searchLower = availableSubjectsSearch.toLowerCase();
    return subject.name.toLowerCase().includes(searchLower) ||
           subject.code.toLowerCase().includes(searchLower) ||
           subject.class.toLowerCase().includes(searchLower);
  });

  // Filter subjects for modal search
  const modalFilteredSubjects = availableSubjects.filter(subject => {
    const searchLower = modalSubjectSearch.toLowerCase();
    return subject.name.toLowerCase().includes(searchLower) ||
           subject.code.toLowerCase().includes(searchLower) ||
           subject.class.toLowerCase().includes(searchLower);
  });

  // Get status color
  const getStatusColor = (status) => {
    const statusObj = statusOptions.find(s => s.value === status);
    return statusObj ? statusObj.color : "secondary";
  };

  // Get status label
  const getStatusLabel = (status) => {
    const statusObj = statusOptions.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  // Handle CSV template download
  const downloadCSVTemplate = () => {
    const csvContent = "Subject Name,Subject Code,Description\nMathematics,MAT101,Mathematics subject\nScience,SCI101,Science subject\nEnglish,ENG101,English subject";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subjects_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showSuccessAlert("Template Downloaded", "CSV template has been downloaded");
  };

  return (
    <div className="subject-management-screen">
      <div className="subject-management-wrapper">
        <Sidebar />
        <div className="subject-management-container">
          {/* Header */}
          <div className="subject-management-header">
            <h1 className="subject-management-title">
              <FontAwesomeIcon icon={faBook} />
              Subject Management System
            </h1>
            <p className="subject-management-subtitle">
              Create, manage and assign subjects to teachers
            </p>
          </div>

          {/* Tabs */}
          <div className="subject-management-tabs">
            <button
              className={`subject-management-tab ${activeTab === "subjects" ? "active" : ""}`}
              onClick={() => setActiveTab("subjects")}
            >
              <FontAwesomeIcon icon={faBook} />
              Manage Subjects
            </button>
            <button
              className={`subject-management-tab ${activeTab === "assign" ? "active" : ""}`}
              onClick={() => setActiveTab("assign")}
            >
              <FontAwesomeIcon icon={faChalkboardTeacher} />
              Assign Subjects
            </button>
            <button
              className={`subject-management-tab ${activeTab === "assignments" ? "active" : ""}`}
              onClick={() => setActiveTab("assignments")}
            >
              <FontAwesomeIcon icon={faList} />
              Assigned Subjects
            </button>
          </div>

          {/* Search and Filter */}
          <div className="subject-management-filters">
            <div className="subject-management-search-box">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder="Search subjects, codes, teachers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="subject-management-search-input"
              />
            </div>
            <div className="subject-management-filter-box">
              <FontAwesomeIcon icon={faFilter} />
              <select
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
                className="subject-management-filter-select"
              >
                <option value="">All Classes</option>
                {classes.map((cls, index) => (
                  <option key={index} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
            {activeTab === "subjects" && (
              <div className="subject-management-filter-box">
                <FontAwesomeIcon icon={faFilter} />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="subject-management-filter-select"
                >
                  <option value="">All Status</option>
                  {statusOptions.map((status, index) => (
                    <option key={index} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Tab Content */}
          <div className="subject-management-content">
            {isLoading && (
              <div className="subject-management-loading-overlay">
                <div className="subject-management-spinner"></div>
                <p>Loading...</p>
              </div>
            )}

            {/* Manage Subjects Tab */}
            {activeTab === "subjects" && (
              <div className="subject-management-tab-content">
                <div className="subject-management-form-card">
                  <div className="subject-management-card-header">
                    <h3 className="subject-management-card-title">
                      <FontAwesomeIcon icon={faPlus} />
                      Create New Subjects
                    </h3>
                    <div className="subject-management-form-tabs">
                      <button 
                        className="subject-management-form-tab active"
                        onClick={() => {}}
                      >
                        <FontAwesomeIcon icon={faCopy} />
                        Bulk Create
                      </button>
                    </div>
                  </div>
                  
                  <form onSubmit={handleBulkCreate} className="subject-management-bulk-form">
                    <div className="subject-management-bulk-fields">
                      <div className="subject-management-bulk-row header">
                        <div className="subject-management-bulk-col">Subject Name *</div>
                        <div className="subject-management-bulk-col">Subject Code *</div>
                        <div className="subject-management-bulk-col">Description</div>
                        <div className="subject-management-bulk-col actions">Actions</div>
                      </div>
                      
                      {bulkSubjects.map((subject, index) => (
                        <div key={index} className="subject-management-bulk-row">
                          <div className="subject-management-bulk-col">
                            <input
                              type="text"
                              value={subject.name}
                              onChange={(e) => updateBulkRow(index, 'name', e.target.value)}
                              placeholder="e.g., Mathematics"
                              required
                            />
                          </div>
                          <div className="subject-management-bulk-col">
                            <input
                              type="text"
                              value={subject.code}
                              onChange={(e) => updateBulkRow(index, 'code', e.target.value)}
                              placeholder="e.g., MATH101"
                              required
                            />
                          </div>
                          <div className="subject-management-bulk-col">
                            <input
                              type="text"
                              value={subject.description}
                              onChange={(e) => updateBulkRow(index, 'description', e.target.value)}
                              placeholder="e.g., Mathematics subject"
                            />
                          </div>
                          <div className="subject-management-bulk-col actions">
                            {index === bulkSubjects.length - 1 ? (
                              <button
                                type="button"
                                onClick={addMoreRows}
                                className="subject-management-btn-add-row"
                                title="Add another row"
                              >
                                <FontAwesomeIcon icon={faPlus} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => removeBulkRow(index)}
                                className="subject-management-btn-remove-row"
                                title="Remove row"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="subject-management-bulk-controls">
                      <div className="subject-management-form-group">
                        <label>Class for All Subjects *</label>
                        <select
                          value={bulkClass}
                          onChange={(e) => setBulkClass(e.target.value)}
                          required
                        >
                          <option value="">Select Class</option>
                          {classes.map((cls, index) => (
                            <option key={index} value={cls}>{cls}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="subject-management-form-group">
                        <label>Status for All Subjects</label>
                        <select
                          value={bulkStatus}
                          onChange={(e) => setBulkStatus(e.target.value)}
                        >
                          {statusOptions.map((status, index) => (
                            <option key={index} value={status.value}>{status.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="subject-management-form-group">
                        <label>Quick Actions</label>
                        <div className="subject-management-quick-actions">
                          <button
                            type="button"
                            onClick={addMoreRows}
                            className="subject-management-btn-secondary"
                          >
                            <FontAwesomeIcon icon={faPlus} />
                            Add More Rows
                          </button>
                          <button
                            type="button"
                            onClick={downloadCSVTemplate}
                            className="subject-management-btn-secondary"
                          >
                            <FontAwesomeIcon icon={faDownload} />
                            Download Template
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="subject-management-form-actions">
                      <button 
                        type="submit" 
                        className="subject-management-btn-primary" 
                        disabled={isLoading}
                      >
                        <FontAwesomeIcon icon={faUpload} />
                        {isLoading ? "Creating..." : `Create ${bulkSubjects.length} Subjects`}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="subject-management-subjects-list-card">
                  <div className="subject-management-card-header">
                    <h3 className="subject-management-card-title">
                      <FontAwesomeIcon icon={faList} />
                      Available Subjects ({filteredSubjects.length})
                    </h3>
                    <div className="subject-management-search-box small">
                      <FontAwesomeIcon icon={faSearch} />
                      <input
                        type="text"
                        placeholder="Search in table..."
                        value={subjectSearch}
                        onChange={(e) => setSubjectSearch(e.target.value)}
                        className="subject-management-search-input"
                      />
                    </div>
                  </div>
                  <div className="subject-management-table-container">
                    <table className="subject-management-subjects-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Subject Name</th>
                          <th>Code</th>
                          <th>Class</th>
                          <th>Status</th>
                          <th>Created At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubjects
                          .filter(subject => 
                            !subjectSearch || 
                            subject.name.toLowerCase().includes(subjectSearch.toLowerCase()) ||
                            subject.code.toLowerCase().includes(subjectSearch.toLowerCase()) ||
                            subject.class.toLowerCase().includes(subjectSearch.toLowerCase())
                          )
                          .length === 0 ? (
                          <tr>
                            <td colSpan="7" className="subject-management-no-data">
                              No subjects found
                            </td>
                          </tr>
                        ) : (
                          filteredSubjects
                            .filter(subject => 
                              !subjectSearch || 
                              subject.name.toLowerCase().includes(subjectSearch.toLowerCase()) ||
                              subject.code.toLowerCase().includes(subjectSearch.toLowerCase()) ||
                              subject.class.toLowerCase().includes(subjectSearch.toLowerCase())
                            )
                            .map((subject) => (
                            <tr key={subject.id}>
                              <td>{subject.id}</td>
                              <td>{subject.name}</td>
                              <td>{subject.code}</td>
                              <td>{subject.class}</td>
                              <td>
                                <span className={`subject-management-status-badge ${getStatusColor(subject.status)}`}>
                                  {getStatusLabel(subject.status)}
                                </span>
                              </td>
                              <td>{new Date(subject.created_at).toLocaleDateString()}</td>
                              <td className="subject-management-actions">
                                <button
                                  onClick={() => handleViewSubject(subject)}
                                  className="subject-management-btn-view"
                                  title="View Details"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </button>
                                <button
                                  onClick={() => handleEditSubject(subject)}
                                  className="subject-management-btn-edit"
                                  title="Edit"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                  onClick={() => handleDeleteSubject(subject.id)}
                                  className="subject-management-btn-delete"
                                  title="Delete"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Assign Subjects Tab */}
            {activeTab === "assign" && (
              <div className="subject-management-tab-content">
                <div className="subject-management-assign-subjects-card">
                  <div className="subject-management-card-header">
                    <h3 className="subject-management-card-title">
                      <FontAwesomeIcon icon={faChalkboardTeacher} />
                      Assign Subjects to Teachers
                    </h3>
                    <div className="subject-management-card-header-actions">
                      <div className="subject-management-search-box small">
                        <FontAwesomeIcon icon={faSearch} />
                        <input
                          type="text"
                          placeholder="Search available subjects..."
                          value={availableSubjectsSearch}
                          onChange={(e) => setAvailableSubjectsSearch(e.target.value)}
                          className="subject-management-search-input"
                        />
                      </div>
                      <button
                        onClick={openAssignmentModal}
                        className="subject-management-btn-primary"
                        disabled={selectedSubjects.length === 0}
                      >
                        <FontAwesomeIcon icon={faLink} />
                        Assign Selected ({selectedSubjects.length})
                      </button>
                    </div>
                  </div>

                  <div className="subject-management-selection-info">
                    <p>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      Select subjects from the list below, then click "Assign Selected" to assign them to a teacher and class.
                    </p>
                  </div>

                  <div className="subject-management-table-container">
                    <table className="subject-management-assign-table">
                      <thead>
                        <tr>
                          <th width="50">
                            <input
                              type="checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSubjects(filteredAvailableSubjects.map(s => s.id));
                                } else {
                                  setSelectedSubjects([]);
                                }
                              }}
                              checked={selectedSubjects.length === filteredAvailableSubjects.length && filteredAvailableSubjects.length > 0}
                            />
                          </th>
                          <th>Subject Name</th>
                          <th>Code</th>
                          <th>Class</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAvailableSubjects.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="subject-management-no-data">
                              No available subjects to assign
                            </td>
                          </tr>
                        ) : (
                          filteredAvailableSubjects.map((subject) => (
                            <tr key={subject.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedSubjects.includes(subject.id)}
                                  onChange={() => handleSubjectSelection(subject.id)}
                                />
                              </td>
                              <td>{subject.name}</td>
                              <td>{subject.code}</td>
                              <td>{subject.class}</td>
                              <td>
                                <span className={`subject-management-status-badge ${getStatusColor(subject.status)}`}>
                                  {getStatusLabel(subject.status)}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Assigned Subjects Tab */}
            {activeTab === "assignments" && (
              <div className="subject-management-tab-content">
                <div className="subject-management-assignments-card">
                  <h3 className="subject-management-card-title">
                    <FontAwesomeIcon icon={faList} />
                    Assigned Subjects ({filteredAssignments.length})
                  </h3>
                  
                  <div className="subject-management-table-container">
                    <table className="subject-management-assignments-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Subject</th>
                          <th>Teacher</th>
                          <th>Class</th>
                          <th>Assigned On</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAssignments.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="subject-management-no-data">
                              No assignments found
                            </td>
                          </tr>
                        ) : (
                          filteredAssignments.map((assignment) => (
                            <tr key={assignment.id}>
                              <td>{assignment.id}</td>
                              <td>
                                <strong>{assignment.subject_name}</strong>
                                <br />
                                <small>Code: {assignment.subject_code}</small>
                              </td>
                              <td>
                                <strong>{assignment.teacher_name}</strong>
                                <br />
                                <small>ID: {assignment.teacher_id}</small>
                              </td>
                              <td>{assignment.class}</td>
                              <td>{new Date(assignment.created_at).toLocaleDateString()}</td>
                              <td className="subject-management-actions">
                                <button
                                  onClick={() => handleRemoveAssignment(assignment.id)}
                                  className="subject-management-btn-delete"
                                  title="Remove Assignment"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="subject-management-modal-overlay">
          <div className="subject-management-modal-content">
            <div className="subject-management-modal-header">
              <h3>
                <FontAwesomeIcon icon={faChalkboardTeacher} />
                Assign Subjects to Teacher
              </h3>
              <button
                onClick={() => {
                  setShowAssignmentModal(false);
                  setTempSelectedSubjects([]);
                  setSelectedTeacher("");
                  setSelectedClass("");
                  setModalSubjectSearch("");
                }}
                className="subject-management-modal-close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="subject-management-modal-body">
              {/* Teacher Selection */}
              <div className="subject-management-modal-section">
                <h4>
                  <FontAwesomeIcon icon={faUserTie} />
                  Select Teacher
                </h4>
                <select
                  value={selectedTeacher}
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                  className="subject-management-modal-select"
                >
                  <option value="">Select a Teacher</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.name} ({teacher.teacher_id})
                    </option>
                  ))}
                </select>
              </div>

              {/* Class Selection */}
              <div className="subject-management-modal-section">
                <h4>
                  <FontAwesomeIcon icon={faGraduationCap} />
                  Select Class for Assignment
                </h4>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="subject-management-modal-select"
                >
                  <option value="">Select Class</option>
                  {classes.map((cls, index) => (
                    <option key={index} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              {/* Selected Subjects List */}
              <div className="subject-management-modal-section">
                <div className="subject-management-modal-section-header">
                  <h4>
                    <FontAwesomeIcon icon={faBook} />
                    Selected Subjects ({tempSelectedSubjects.length})
                  </h4>
                  {tempSelectedSubjects.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setTempSelectedSubjects([])}
                      className="subject-management-btn-secondary small"
                    >
                      <FontAwesomeIcon icon={faTimesCircle} />
                      Clear All
                    </button>
                  )}
                </div>
                <div className="subject-management-selected-subjects-list">
                  {tempSelectedSubjects.map((subjectId) => {
                    const subject = subjects.find(s => s.id === subjectId);
                    return subject ? (
                      <div key={subjectId} className="subject-management-selected-subject-item">
                        <span className="subject-management-subject-name">{subject.name}</span>
                        <span className="subject-management-subject-code">{subject.code}</span>
                        <span className="subject-management-subject-class">{subject.class}</span>
                        <button
                          onClick={() => handleTempSubjectSelection(subjectId)}
                          className="subject-management-remove-subject"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>

              {/* Available Subjects for adding more */}
              <div className="subject-management-modal-section">
                <div className="subject-management-modal-section-header">
                  <h4>
                    <FontAwesomeIcon icon={faPlus} />
                    Add More Subjects
                  </h4>
                  <div className="subject-management-search-box small">
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                      type="text"
                      placeholder="Search subjects..."
                      value={modalSubjectSearch}
                      onChange={(e) => setModalSubjectSearch(e.target.value)}
                      className="subject-management-search-input"
                    />
                  </div>
                </div>
                <div className="subject-management-add-subjects-list">
                  {modalFilteredSubjects
                    .filter(subject => !tempSelectedSubjects.includes(subject.id))
                    .map((subject) => (
                      <div key={subject.id} className="subject-management-add-subject-item">
                        <input
                          type="checkbox"
                          id={`subject-${subject.id}`}
                          checked={tempSelectedSubjects.includes(subject.id)}
                          onChange={() => handleTempSubjectSelection(subject.id)}
                        />
                        <label htmlFor={`subject-${subject.id}`}>
                          <span className="subject-management-modal-subject-name">{subject.name}</span>
                          <span className="subject-management-modal-subject-code">({subject.code})</span>
                          <span className="subject-management-modal-subject-class">{subject.class}</span>
                          <span className={`subject-management-status-badge small ${getStatusColor(subject.status)}`}>
                            {getStatusLabel(subject.status)}
                          </span>
                        </label>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="subject-management-modal-footer">
              <button
                onClick={() => {
                  setShowAssignmentModal(false);
                  setTempSelectedSubjects([]);
                  setSelectedTeacher("");
                  setSelectedClass("");
                  setModalSubjectSearch("");
                }}
                className="subject-management-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignSubjects}
                className="subject-management-btn-primary"
                disabled={!selectedTeacher || !selectedClass || tempSelectedSubjects.length === 0}
              >
                <FontAwesomeIcon icon={faCheck} />
                Assign Subjects
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Modal - Now styled like assignment modal */}
      {showUpdateModal && (
        <div className="subject-management-modal-overlay">
          <div className="subject-management-modal-content" style={{ maxWidth: '600px' }}>
            <div className="subject-management-modal-header">
              <h3>
                <FontAwesomeIcon icon={faEdit} />
                {editMode ? "Update Subject" : "Create New Subject"}
              </h3>
              <button
                onClick={() => {
                  setShowUpdateModal(false);
                  setEditMode(false);
                  setEditingSubject(null);
                  setNewSubject({ 
                    name: "", 
                    code: "", 
                    class: "", 
                    description: "", 
                    status: "active" 
                  });
                }}
                className="subject-management-modal-close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="subject-management-modal-body">
              <form onSubmit={handleSubjectSubmit} className="subject-management-update-form">
                <div className="subject-management-modal-section">
                  <div className="subject-management-form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="subject-management-form-group">
                      <label>Subject Name *</label>
                      <input
                        type="text"
                        value={newSubject.name}
                        onChange={(e) => setNewSubject({...newSubject, name: e.target.value})}
                        placeholder="Enter subject name"
                        required
                      />
                    </div>
                    <div className="subject-management-form-group">
                      <label>Subject Code *</label>
                      <input
                        type="text"
                        value={newSubject.code}
                        onChange={(e) => setNewSubject({...newSubject, code: e.target.value})}
                        placeholder="Enter subject code"
                        required
                      />
                    </div>
                    <div className="subject-management-form-group">
                      <label>Class *</label>
                      <select
                        value={newSubject.class}
                        onChange={(e) => setNewSubject({...newSubject, class: e.target.value})}
                        required
                      >
                        <option value="">Select Class</option>
                        {classes.map((cls, index) => (
                          <option key={index} value={cls}>{cls}</option>
                        ))}
                      </select>
                    </div>
                    <div className="subject-management-form-group">
                      <label>Status</label>
                      <select
                        value={newSubject.status}
                        onChange={(e) => setNewSubject({...newSubject, status: e.target.value})}
                      >
                        {statusOptions.map((status, index) => (
                          <option key={index} value={status.value}>{status.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="subject-management-form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Description</label>
                    <textarea
                      value={newSubject.description}
                      onChange={(e) => setNewSubject({...newSubject, description: e.target.value})}
                      placeholder="Enter subject description"
                      rows="4"
                    />
                  </div>
                </div>
                
                <div className="subject-management-modal-footer">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUpdateModal(false);
                      setEditMode(false);
                      setEditingSubject(null);
                      setNewSubject({ 
                        name: "", 
                        code: "", 
                        class: "", 
                        description: "", 
                        status: "active" 
                      });
                    }}
                    className="subject-management-btn-secondary"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    Cancel
                  </button>
                  <button type="submit" className="subject-management-btn-primary" disabled={isLoading}>
                    <FontAwesomeIcon icon={faSave} />
                    {isLoading ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update Subject" : "Create Subject")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal - Now styled like assignment modal */}
      {showViewModal && viewingSubject && (
        <div className="subject-management-modal-overlay">
          <div className="subject-management-modal-content" style={{ maxWidth: '600px' }}>
            <div className="subject-management-modal-header">
              <h3>
                <FontAwesomeIcon icon={faEye} />
                Subject Details
              </h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingSubject(null);
                }}
                className="subject-management-modal-close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="subject-management-modal-body">
              <div className="subject-management-view-details-grid">
                <div className="subject-management-detail-item">
                  <label>Subject Name:</label>
                  <div className="subject-management-detail-value">{viewingSubject.name}</div>
                </div>
                <div className="subject-management-detail-item">
                  <label>Subject Code:</label>
                  <div className="subject-management-detail-value subject-code">{viewingSubject.code}</div>
                </div>
                <div className="subject-management-detail-item">
                  <label>Class:</label>
                  <div className="subject-management-detail-value">{viewingSubject.class}</div>
                </div>
                <div className="subject-management-detail-item">
                  <label>Status:</label>
                  <div className="subject-management-detail-value">
                    <span className={`subject-management-status-badge ${getStatusColor(viewingSubject.status)}`}>
                      {getStatusLabel(viewingSubject.status)}
                    </span>
                  </div>
                </div>
                {viewingSubject.description && (
                  <div className="subject-management-detail-item full-width">
                    <label>Description:</label>
                    <div className="subject-management-detail-value description">
                      {viewingSubject.description}
                    </div>
                  </div>
                )}
                <div className="subject-management-detail-item">
                  <label>Created At:</label>
                  <div className="subject-management-detail-value">
                    {new Date(viewingSubject.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                {viewingSubject.updated_at && (
                  <div className="subject-management-detail-item">
                    <label>Last Updated:</label>
                    <div className="subject-management-detail-value">
                      {new Date(viewingSubject.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="subject-management-modal-footer">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingSubject(null);
                  }}
                  className="subject-management-btn-secondary"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditSubject(viewingSubject);
                  }}
                  className="subject-management-btn-primary"
                >
                  <FontAwesomeIcon icon={faEdit} />
                  Edit Subject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectManagement;